import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useVolunteerAuth } from '../../hooks/useVolunteerAuth';
import { signInWithGoogle, signInWithMicrosoft, signInWithYahoo } from '../../services/volAuth';
import './Vol.css';

function VolLogin() {
  const { firebaseUser, volunteer, volLoading } = useVolunteerAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');
  const [signingIn, setSigningIn] = useState(false);

  const notFound = searchParams.get('notfound') === '1';

  useEffect(() => {
    if (volLoading || firebaseUser === undefined) return;
    if (!firebaseUser) return;
    if (!volunteer) return;
    if (volunteer.approvalStatus === 'approved') {
      navigate('/vol/dashboard', { replace: true });
    } else {
      navigate('/vol/pending', { replace: true });
    }
  }, [firebaseUser, volunteer, volLoading, navigate]);

  const handleSignIn = async (providerFn, label) => {
    setError('');
    setSigningIn(true);
    try {
      const { volunteer: vol } = await providerFn();
      if (!vol) {
        setError('Your email address was not found in the volunteer registry. Please contact the scheduling team.');
        setSigningIn(false);
        return;
      }
      // Navigation handled by the useEffect above after context refreshes
    } catch (err) {
      if (err.code === 'auth/popup-closed-by-user') {
        // user dismissed — no error shown
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setError('An account already exists with this email using a different sign-in method. Try a different option below.');
      } else {
        setError(`Sign-in failed: ${err.message}`);
      }
      setSigningIn(false);
    }
  };

  return (
    <div className="vol-login-page">
      <div className="vol-login-card">
        <div className="vol-login-logo">
          <div className="vol-login-logo-icon">✦</div>
          <h1>SMPW Masters</h1>
          <p>Volunteer Portal</p>
        </div>

        <p className="vol-login-subtitle">
          Sign in with the email address you used to register as a volunteer.
        </p>

        {notFound && !error && (
          <div className="vol-alert vol-alert-warn">
            Your email address was not found in the volunteer registry. Please contact the scheduling team.
          </div>
        )}

        {error && (
          <div className="vol-alert vol-alert-error">{error}</div>
        )}

        <div className="vol-login-buttons">
          <button
            className="vol-oauth-btn vol-oauth-google"
            onClick={() => handleSignIn(signInWithGoogle, 'Google')}
            disabled={signingIn}
          >
            <GoogleIcon />
            Continue with Google
          </button>

          <button
            className="vol-oauth-btn vol-oauth-microsoft"
            onClick={() => handleSignIn(signInWithMicrosoft, 'Microsoft')}
            disabled={signingIn}
          >
            <MicrosoftIcon />
            Continue with Microsoft
          </button>

          <button
            className="vol-oauth-btn vol-oauth-yahoo"
            onClick={() => handleSignIn(signInWithYahoo, 'Yahoo')}
            disabled={signingIn}
          >
            <YahooIcon />
            Continue with Yahoo
          </button>
        </div>

        {signingIn && <p className="vol-login-spinner">Signing in…</p>}

        <p className="vol-login-footer">
          Not registered yet? <a href="/volunteer">Sign up as a volunteer</a>
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 6.29C4.672 4.163 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#00a4ef"/>
      <rect x="1" y="11" width="9" height="9" fill="#7fba00"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

function YahooIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" fill="#6001d2">
      <path d="M0 0l6.245 12.876L0 24h4.233l3.895-7.58L12 24h4.233L5.89 0H0zm15.38 0l-3.125 6.25L15.38 12.5h4.346L22.8 6.25 19.727 0H15.38z"/>
    </svg>
  );
}

export default VolLogin;
